import React, { useRef, useEffect, useState } from 'react';
import './VideoLauncher.css';

const VideoLauncher = ({ onVideoEnd }) => {
  const videoRef = useRef(null);
  const [showSkip, setShowSkip] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progressPct, setProgressPct] = useState(null);
  const [bitrateMbps, setBitrateMbps] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const handleEnded = () => onVideoEnd?.();
    const handlePlaying = () => {
      setTimeout(() => setShowSkip(true), 3000);
    };
    const handleCanPlayThrough = () => {
      setIsLoading(false);
    };

    el.addEventListener('ended', handleEnded);
    el.addEventListener('playing', handlePlaying);
    el.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('playing', handlePlaying);
      el.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, [onVideoEnd]);

  // Stream the video via fetch to measure progress and estimate throughput (Mbps)
  useEffect(() => {
    let abort = false;
    const controller = new AbortController();
    const srcUrl = '/assets/intro4.mp4';
    let objectUrl = null;

    async function streamAndLoad() {
      try {
        const resp = await fetch(srcUrl, { signal: controller.signal });
        if (!resp.ok || !resp.body) throw new Error('fetch failed');

        const contentLengthHeader = resp.headers.get('content-length');
        const total = contentLengthHeader ? parseInt(contentLengthHeader, 10) : null;
        const reader = resp.body.getReader();
        const chunks = [];
        let received = 0;
        const start = Date.now();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (abort) { controller.abort(); break; }
          chunks.push(value);
          received += value.length;
          const elapsedSec = Math.max(0.001, (Date.now() - start) / 1000);
          const mbps = (received * 8) / (elapsedSec * 1e6); // megabits/s
          setBitrateMbps(mbps);
          if (total) {
            const pct = Math.round((received / total) * 100);
            setProgressPct(pct);
          } else {
            setProgressPct(null);
          }
        }

        if (chunks.length === 0) throw new Error('no data');
        const blob = new Blob(chunks, { type: 'video/mp4' });
        objectUrl = URL.createObjectURL(blob);
        setVideoSrc(objectUrl);
        setIsLoading(false);
        // auto-play when ready; handle rejected promise (benign AbortError when src changes)
        try {
          const p = videoRef.current && videoRef.current.play();
          if (p && p.catch) {
            p.catch(err => {
              // Ignore AbortError caused by a new load/cleanup; log others
              if (err && err.name === 'AbortError') return;
              console.warn('video play() failed:', err);
            });
          }
        } catch (e) {}
      } catch (e) {
        // If aborted, don't treat as an error
        if (e && (e.name === 'AbortError' || e.message === 'The user aborted a request.')) {
          console.info('streaming aborted');
          return;
        }
        // fallback: use direct src for other errors
        console.warn('streaming failed, fallback to direct src', e);
        setVideoSrc(srcUrl);
        setIsLoading(false);
      }
    }

    streamAndLoad();

    return () => {
      abort = true;
      controller.abort();
      // ensure video is paused to prevent play() rejections
      try { if (videoRef.current) { videoRef.current.pause(); videoRef.current.src = ''; } } catch (e) {}
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const handleSkip = () => {
    const el = videoRef.current;
    if (el) {
      try {
        el.pause();
        if (Number.isFinite(el.duration)) {
          el.currentTime = el.duration;
        }
      } catch {}
    }
    onVideoEnd?.();
  };

  return (
    <>
      {showSkip && (
        <button
          className="skip-intro-btn"
          onClick={handleSkip}
          type="button"
        >
          Skip intro
        </button>
      )}
      <div className="video-container">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-line">Loading...</div>
            <div className="progress-meter">
              <div className="progress-bar" style={{ width: progressPct != null ? `${progressPct}%` : '2%' }} />
            </div>
            <div className="loading-stats">
              <span>{progressPct != null ? `${progressPct}%` : '–'} </span>
              <span className="dot">•</span>
              <span>{bitrateMbps != null ? `${bitrateMbps.toFixed(2)} Mb/s` : 'estimating...'}</span>
            </div>
          </div>
        )}
        <video
          ref={videoRef}
          src={videoSrc || undefined}
          autoPlay
          muted
          playsInline
          width="100%"
          className={`fullscreen-video ${isLoading ? 'hidden' : ''}`}
        />
      </div>
    </>
  );
};

export default VideoLauncher;
